import { onAuthStateChanged, type User as FirebaseUser } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { useAuthStore } from "@/stores/auth-store";

export class AuthSyncTimeoutError extends Error {
  constructor() {
    super("auth-sync-timeout");
    this.name = "AuthSyncTimeoutError";
  }
}

function matchesUser(u: FirebaseUser | null, expectedUid?: string): u is FirebaseUser {
  if (!u) return false;
  return !expectedUid || u.uid === expectedUid;
}

/**
 * Waits until Firebase auth and the Zustand auth store agree the user is signed in.
 * Avoids navigating to /app before ProtectedRoute sees `isSignedIn` (common on mobile).
 */
export function waitForAuthSynced(expectedUid?: string, timeoutMs = 8000): Promise<FirebaseUser> {
  const { initialized, firebaseUser } = useAuthStore.getState();
  if (initialized && matchesUser(firebaseUser, expectedUid)) {
    return Promise.resolve(firebaseUser);
  }
  if (matchesUser(auth.currentUser, expectedUid)) {
    return Promise.resolve(auth.currentUser);
  }

  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new AuthSyncTimeoutError());
    }, timeoutMs);

    const tryResolve = (u: FirebaseUser | null) => {
      if (!matchesUser(u, expectedUid)) return;
      cleanup();
      resolve(u);
    };

    const unsubStore = useAuthStore.subscribe((state) => {
      if (state.initialized) tryResolve(state.firebaseUser);
    });

    const unsubAuth = onAuthStateChanged(auth, tryResolve);

    function cleanup() {
      window.clearTimeout(timeoutId);
      unsubStore();
      unsubAuth();
    }

    const latest = useAuthStore.getState();
    if (latest.initialized) tryResolve(latest.firebaseUser);
    tryResolve(auth.currentUser);
  });
}
